import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'
import { mapRawStudiosToStore } from '@/lib/studioMapper'
import {
  createEmptyTrainerChecklistCriterionPhotos,
  type TrainerChecklistCriterionPhotos,
  type TrainerChecklistPhoto,
} from '@/lib/trainerChecklistReviews'
import type { TrainerChecklistComments, TrainerChecklistScores } from '@/lib/trainerChecklist'
import type { Studio } from '@/types'
import { getTrainerReviewPhotoUrl, resolveTrainerReviewPhotoUrls } from '@/services/trainerReviewPhotoUrls'

export interface TrainerReviewDirectionOption {
  directionId: string
  name: string
}

export async function listTrainerReviewAvailableStudios(): Promise<Studio[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('list_trainer_review_available_studios')
  )

  if (error) throw new Error(formatError(error))

  return mapRawStudiosToStore(data ?? [])
}

export type TrainerReviewSource = 'management_company' | 'senior_teacher'

export interface TrainerReviewSummaryRow {
  reviewId: string
  teacherId: string | null
  reviewerUserId: string
  reviewerDisplayName: string
  studioInternalId: number | null
  cityName: string
  studioAddress: string
  reviewDate: string
  trainingAt: string
  directionId: string
  directionName: string
  coachName: string
  generalComment: string
  totalScore: number
  maxScore: number
  qualityPercent: number
  createdAt: string
  updatedAt: string
  photosCount: number
  reviewSource: TrainerReviewSource
}

export interface TrainerReviewDetail {
  reviewId: string
  teacherId: string | null
  meta: {
    studioId: string
    city: string
    studioAddress: string
    reviewDate: string
    trainingDateTime: string
    directionId: string
    directionName: string
    coachName: string
  }
  scores: TrainerChecklistScores
  comments: TrainerChecklistComments
  generalComment: string
  generalPhotos: TrainerChecklistPhoto[]
  criterionPhotos: TrainerChecklistCriterionPhotos
  scoreReviewIds: Record<string, string>
  totalScore: number
  maxScore: number
  qualityPercent: number
  createdAt: string
  updatedAt: string
}

function formatError(error: unknown): string {
  if (!error) return 'Unknown error'
  if (error instanceof Error) return error.message
  if (typeof error === 'object') {
    const e = error as { message?: string; details?: string; hint?: string; code?: string }
    return [e.message, e.details, e.hint, e.code].filter(Boolean).join(' | ') || JSON.stringify(error)
  }
  return String(error)
}

function text(value: unknown, fallback = ''): string {
  return value == null ? fallback : String(value)
}

function optionalText(value: unknown): string | undefined {
  return value == null ? undefined : String(value)
}

function rows(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value)
    ? value.filter(item => item != null && typeof item === 'object') as Array<Record<string, unknown>>
    : []
}

async function getTrainingDisplay(reviewIds: Array<string | number>): Promise<Map<string, string>> {
  const refs = [...new Set(reviewIds.map(String).filter(Boolean))]
  if (!refs.length) return new Map()

  const { data, error } = await withAuthRecovery(() =>
    (getSupabase() as any).rpc('get_trainer_review_training_display', { p_review_refs: refs })
  )
  if (error) throw new Error(formatError(error))

  const resultRows = Array.isArray(data) ? data as Array<Record<string, unknown>> : []
  return new Map(resultRows.flatMap(row => {
    const local = row.training_local_at == null ? '' : String(row.training_local_at).slice(0, 16)
    return row.review_ref == null || !local ? [] : [[String(row.review_ref), local] as const]
  }))
}

function sanitizeFileName(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase() || 'photo'
}

export async function listTrainerReviewDirections(): Promise<TrainerReviewDirectionOption[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('list_trainer_review_directions')
  )

  if (error) throw new Error(formatError(error))

  return (data ?? []).map(row => ({
    directionId: String(row.direction_id),
    name: text(row.name),
  }))
}

export async function ensureTrainerReviewDirection(name: string): Promise<string> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('ensure_trainer_review_direction', { p_name: name })
  )

  if (error) throw new Error(formatError(error))
  return String(data)
}

export async function listTrainerReviewSummary(params: {
  reviewerUserId?: string | null
  month?: string | null
  search?: string
  limit?: number
  offset?: number
} = {}): Promise<TrainerReviewSummaryRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('list_trainer_review_summary_v2', {
      p_reviewer_user_id: params.reviewerUserId ?? null,
      p_month: params.month ?? null,
      p_search: params.search?.trim() || null,
      p_limit: params.limit ?? 500,
      p_offset: params.offset ?? 0,
    })
  )

  if (error) throw new Error(formatError(error))

  const resultRows = (data ?? []) as Array<Record<string, unknown>>
  const display = await getTrainingDisplay(resultRows.map(row => row.review_id as string | number))
  return resultRows.map(row => ({
    reviewId: String(row.review_id),
    teacherId: row.teacher_id == null ? null : String(row.teacher_id),
    reviewerUserId: String(row.reviewer_user_id),
    reviewerDisplayName: text(row.reviewer_display_name),
    studioInternalId: row.studio_internal_id == null ? null : Number(row.studio_internal_id),
    cityName: text(row.city_name),
    studioAddress: text(row.studio_address),
    reviewDate: text(row.review_date),
    trainingAt: display.get(String(row.review_id)) ?? '',
    directionId: row.direction_id == null ? '' : String(row.direction_id),
    directionName: text(row.direction_name),
    coachName: text(row.coach_name),
    generalComment: text(row.general_comment),
    totalScore: Number(row.total_score ?? 0),
    maxScore: Number(row.max_score ?? 0),
    qualityPercent: Number(row.quality_percent ?? 0),
    createdAt: text(row.created_at),
    updatedAt: text(row.updated_at),
    photosCount: Number(row.photos_count ?? 0),
    reviewSource: row.review_source === 'senior_teacher' ? 'senior_teacher' : 'management_company',
  }))
}

export async function listAllTrainerReviewSummary(params: {
  reviewerUserId?: string | null
  month?: string | null
  search?: string
} = {}): Promise<TrainerReviewSummaryRow[]> {
  const pageSize = 500
  const rows: TrainerReviewSummaryRow[] = []
  let offset = 0
  let hasMore = true

  while (hasMore) {
    const batch = await listTrainerReviewSummary({
      ...params,
      limit: pageSize,
      offset,
    })
    rows.push(...batch)
    hasMore = batch.length === pageSize
    offset += batch.length
  }

  return rows
}

/**
 * Личный реестр: свои отсмотры, независимо от того, в какое хранилище лёг
 * результат.
 *
 * Отдельная RPC, а не параметр к `list_trainer_review_summary`: у той есть
 * `p_reviewer_user_id`, через который тот же вызов обслуживает и руководителя.
 * Добавлять туда объединение с контрольными результатами значило бы повесить
 * секретность на одно условие в функции, которая отдаёт данные и тем, от кого
 * их прячут. У `list_my_trainer_review_summary` параметра скоупа нет вовсе.
 *
 * `entry_key` — непрозрачный ключ строки; сервер не отдаёт ни `review_id`, ни
 * `assignment_id`, чтобы по пустому идентификатору нельзя было отличить вид
 * результата. Здесь он кладётся в `reviewId`: для ссылок и ключей списка
 * ничего другого и не нужно.
 *
 * `selfUserId` передаётся клиентом и никак не влияет на выборку — сервер и так
 * возвращает только своё. Он нужен лишь фильтру по автору на экране, который
 * в этом режиме всегда сводится к одному человеку.
 */
export async function listMyTrainerReviewSummary(
  selfUserId: string,
  params: { month?: string | null; search?: string; teacherId?: string | null } = {}
): Promise<TrainerReviewSummaryRow[]> {
  const supabase = getSupabase()
  const pageSize = 500
  const rows: TrainerReviewSummaryRow[] = []
  let offset = 0
  let hasMore = true

  while (hasMore) {
    const { data, error } = await withAuthRecovery(() =>
      supabase.rpc('list_my_trainer_review_summary', {
        p_month: params.month ?? null,
        p_search: params.search?.trim() || null,
        p_teacher_id: params.teacherId ?? null,
        p_limit: pageSize,
        p_offset: offset,
      })
    )

    if (error) throw new Error(formatError(error))

    const rawBatch = (data ?? []) as Array<Record<string, unknown>>
    const reviewRefs = rawBatch.map(row => text(row.entry_key)).filter(Boolean)
    const trainingDisplay = await getTrainingDisplay(reviewRefs)

    const batch = rawBatch.map(row => {
      const entryKey = text(row.entry_key)
      return ({
      reviewId: String(row.entry_key),
      teacherId: row.teacher_id == null ? null : String(row.teacher_id),
      reviewerUserId: selfUserId,
      reviewerDisplayName: text(row.reviewer_display_name),
      studioInternalId: row.studio_internal_id == null ? null : Number(row.studio_internal_id),
      cityName: text(row.city_name),
      studioAddress: text(row.studio_address),
      reviewDate: text(row.review_date),
      trainingAt: trainingDisplay.get(entryKey) ?? '',
      directionId: row.direction_id == null ? '' : String(row.direction_id),
      directionName: text(row.direction_name),
      coachName: text(row.coach_name),
      generalComment: text(row.general_comment),
      totalScore: Number(row.total_score ?? 0),
      maxScore: Number(row.max_score ?? 0),
      qualityPercent: Number(row.quality_percent ?? 0),
      createdAt: text(row.created_at),
      updatedAt: text(row.updated_at),
      photosCount: Number(row.photos_count ?? 0),
      reviewSource: 'management_company' as const,
      })
    })

    rows.push(...batch)
    hasMore = batch.length === pageSize
    offset += batch.length
  }

  return rows
}

/**
 * Ключ строки личного реестра указывает на контрольный результат, а не на
 * обычный отсмотр.
 *
 * Живёт здесь, а не в компоненте: это свойство формата ключа, который
 * назначает сервер. В интерфейсе по этому признаку ничего не подписывается —
 * он нужен только чтобы выбрать рабочий маршрут вместо ломающегося.
 */
export async function archiveTrainerReview(reviewId: string): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    supabase.rpc('archive_trainer_review', {
      p_review_id: Number(reviewId),
    })
  )

  if (error) {
    throw new Error(`Не удалось удалить отсмотр: ${formatError(error)}`)
  }
}

function mapPhotoRowToPhoto(
  photoRow: Record<string, unknown>,
  resolvedPhotoUrls: Map<string, string>,
): TrainerChecklistPhoto {
  const storageBucket = text(photoRow.storage_bucket, 'trainer-review-photos')
  const storagePath = text(photoRow.storage_path)
  const previewUrl = storagePath
    ? getTrainerReviewPhotoUrl(photoRow, resolvedPhotoUrls)
    : text(photoRow.external_url)

  const photoKey = optionalText(photoRow.photo_key)
  const photoId = photoRow.photo_id == null ? undefined : Number(photoRow.photo_id)
  return {
    id: `photo-${photoKey ?? photoId ?? crypto.randomUUID()}`,
    photoId,
    photoKey: photoKey ?? undefined,
    reviewScoreId: photoRow.review_score_id == null ? undefined : Number(photoRow.review_score_id),
    name: text(photoRow.original_name, 'Фото'),
    previewUrl,
    storageBucket,
    storagePath,
    mimeType: optionalText(photoRow.mime_type),
    sizeBytes: photoRow.size_bytes == null ? undefined : Number(photoRow.size_bytes),
    widthPx: photoRow.width_px == null ? undefined : Number(photoRow.width_px),
    heightPx: photoRow.height_px == null ? undefined : Number(photoRow.height_px),
  } satisfies TrainerChecklistPhoto
}

/**
 * Печатная форма своей строки личного реестра — обычного отсмотра или
 * контрольного результата, одинаково.
 *
 * §9 плана требует, чтобы личная печатная форма не различалась. Поэтому одна
 * серверная функция и одна форма ответа: клиент не выбирает разметку по виду
 * результата, потому что вид ему и не приходит.
 *
 * Wave C нормализует и оценки, и фотографии до одинаковых UUID-полей. Даже
 * числовые ID разных таблиц в этот ответ не попадают.
 */
export async function getMyTrainerReviewEntryDetail(entryKey: string): Promise<TrainerReviewDetail | null> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('get_my_trainer_review_entry_detail', { p_entry_key: entryKey })
  )

  if (error) throw new Error(formatError(error))

  const row = ((data ?? []) as Array<Record<string, unknown>>)[0]
  if (!row) return null

  const scores = Object.create(null) as TrainerChecklistScores
  const comments = Object.create(null) as TrainerChecklistComments
  const criterionPhotos = createEmptyTrainerChecklistCriterionPhotos()
  const scoreReviewIds: Record<string, string> = {}
  const scoreRows = rows(row.scores)
  const generalPhotoRows = rows(row.photos)
  const photoRows = [
    ...generalPhotoRows,
    ...scoreRows.flatMap(scoreRow => rows(scoreRow.photos)),
  ]
  const resolvedPhotoUrls = await resolveTrainerReviewPhotoUrls(photoRows)

  for (const scoreRow of scoreRows) {
    const criterionCode = text(scoreRow.criterion_code)
    if (!criterionCode) continue
    scores[criterionCode] = scoreRow.score == null ? null : Number(scoreRow.score)
    comments[criterionCode] = text(scoreRow.comment)
    if (scoreRow.review_score_id != null) {
      scoreReviewIds[criterionCode] = String(scoreRow.review_score_id)
    }
    criterionPhotos[criterionCode] = rows(scoreRow.photos)
      .map(photoRow => mapPhotoRowToPhoto(photoRow, resolvedPhotoUrls))
  }

  const generalPhotos = generalPhotoRows.map(photoRow => mapPhotoRowToPhoto(photoRow, resolvedPhotoUrls))
  const displayEntryKey = String(row.entry_key)
  const trainingDisplay = await getTrainingDisplay([displayEntryKey])

  return {
    reviewId: displayEntryKey,
    teacherId: row.teacher_id == null ? null : String(row.teacher_id),
    meta: {
      studioId: row.studio_internal_id == null ? '' : String(row.studio_internal_id),
      city: text(row.city_name),
      studioAddress: text(row.studio_address),
      reviewDate: text(row.review_date),
      trainingDateTime: trainingDisplay.get(displayEntryKey) ?? '',
      directionId: row.direction_id == null ? '' : String(row.direction_id),
      directionName: text(row.direction_name),
      coachName: text(row.coach_name),
    },
    scores,
    comments,
    generalComment: text(row.general_comment),
    generalPhotos,
    criterionPhotos,
    scoreReviewIds,
    totalScore: Number(row.total_score ?? 0),
    maxScore: Number(row.max_score ?? 0),
    qualityPercent: Number(row.quality_percent ?? 0),
    createdAt: text(row.created_at),
    updatedAt: text(row.updated_at),
  }
}

export async function getTrainerReviewDetail(reviewId: string): Promise<TrainerReviewDetail | null> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('get_trainer_review_detail', { p_review_id: Number(reviewId) })
  )

  if (error) throw new Error(formatError(error))

  const detailRows = data ?? []
  const row = detailRows[0]
  if (!row) return null

  const scores = Object.create(null) as TrainerChecklistScores
  const comments = Object.create(null) as TrainerChecklistComments
  const criterionPhotos = createEmptyTrainerChecklistCriterionPhotos()
  const scoreReviewIds: Record<string, string> = {}
  const scoreRows = rows(row.scores)
  const generalPhotoRows = rows(row.general_photos).length > 0 ? rows(row.general_photos) : rows(row.photos)
  const photoRows = [
    ...generalPhotoRows,
    ...scoreRows.flatMap(scoreRow => rows(scoreRow.photos)),
  ]
  const resolvedPhotoUrls = await resolveTrainerReviewPhotoUrls(photoRows)
  for (const scoreRow of scoreRows) {
    const criterionCode = text(scoreRow.criterion_code)
    if (!criterionCode) continue
    scores[criterionCode] = scoreRow.score == null ? null : Number(scoreRow.score)
    comments[criterionCode] = text(scoreRow.comment)
    if (scoreRow.review_score_id != null) {
      scoreReviewIds[criterionCode] = String(scoreRow.review_score_id)
    }

    const photoRows = rows(scoreRow.photos)
    criterionPhotos[criterionCode] = photoRows
      .map(photoRow => mapPhotoRowToPhoto(photoRow, resolvedPhotoUrls))
  }

  const generalPhotos = generalPhotoRows.map(photoRow => mapPhotoRowToPhoto(photoRow, resolvedPhotoUrls))
  const trainingDisplay = await getTrainingDisplay([reviewId])

  return {
    reviewId: String(row.review_id),
    teacherId: row.teacher_id == null ? null : String(row.teacher_id),
    meta: {
      studioId: row.studio_internal_id == null ? '' : String(row.studio_internal_id),
      city: text(row.city_name),
      studioAddress: text(row.studio_address),
      reviewDate: text(row.review_date),
      trainingDateTime: trainingDisplay.get(reviewId) ?? '',
      directionId: row.direction_id == null ? '' : String(row.direction_id),
      directionName: text(row.direction_name),
      coachName: text(row.coach_name),
    },
    scores,
    comments,
    generalComment: text(row.general_comment),
    generalPhotos,
    criterionPhotos,
    scoreReviewIds,
    totalScore: Number(row.total_score ?? 0),
    maxScore: Number(row.max_score ?? 0),
    qualityPercent: Number(row.quality_percent ?? 0),
    createdAt: text(row.created_at),
    updatedAt: text(row.updated_at),
  }
}

export async function upsertTrainerReview(input: {
  reviewId?: string | null
  studioInternalId?: number | null
  cityName?: string | null
  studioAddress?: string | null
  /** Дата отсмотра. `null` — не менять её у существующей записи (сервер: COALESCE). */
  reviewDate: string | null
  trainingAt?: string | null
  directionId: string
  coachName: string
  teacherId?: string | null
  generalComment?: string | null
  scores: Array<{ criterionCode: string; score: number | null; comment?: string | null }>
}): Promise<string> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('upsert_trainer_review_v2', {
      p_review_id: input.reviewId ? Number(input.reviewId) : null,
      p_studio_internal_id: input.studioInternalId ?? null,
      p_city_name: input.cityName ?? null,
      p_studio_address: input.studioAddress ?? null,
      p_review_date: input.reviewDate || null,
      p_training_at: input.trainingAt || null,
      p_direction_id: Number(input.directionId),
      p_coach_name: input.coachName,
      p_teacher_id: input.teacherId || null,
      p_general_comment: input.generalComment ?? null,
      p_scores: input.scores.map(score => ({
        criterion_code: score.criterionCode,
        score: score.score,
        comment: score.comment ?? null,
      })),
    })
  )

  if (error) throw new Error(`Не удалось сохранить карточку отсмотра: ${formatError(error)}`)
  return String(data)
}

export async function upsertMyTrainerReviewEntry(input: {
  entryKey: string
  directionId: string
  coachName: string
  teacherId: string
  generalComment?: string | null
  scores: Array<{ criterionCode: string; score: number | null; comment?: string | null }>
}): Promise<string> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('save_my_trainer_review_entry', {
      p_entry_key: input.entryKey,
      p_direction_id: Number(input.directionId),
      p_teacher_id: input.teacherId,
      p_coach_name: input.coachName,
      p_general_comment: input.generalComment ?? null,
      p_scores: input.scores.map(score => ({
        criterion_code: score.criterionCode,
        score: score.score,
        comment: score.comment ?? null,
      })),
    })
  )
  if (error) throw new Error(`Не удалось сохранить карточку отсмотра: ${formatError(error)}`)
  return String(data)
}

export async function uploadTrainerReviewPhoto(params: {
  reviewId: string
  reviewScoreId?: string | null
  photo: TrainerChecklistPhoto
  sortOrder: number
}): Promise<TrainerChecklistPhoto> {
  const { reviewId, reviewScoreId, photo, sortOrder } = params
  if (!photo.file) return photo

  const supabase = getSupabase()
  const extension = photo.file.name.includes('.') ? photo.file.name.split('.').pop() : 'jpg'
  const storagePath = `${reviewId}/${Date.now()}-${Math.random().toString(16).slice(2)}-${sanitizeFileName(photo.file.name || `photo.${extension}`)}`

  const { error: uploadError } = await withAuthRecovery(() =>
    supabase.storage.from('trainer-review-photos').upload(storagePath, photo.file!, {
      upsert: false,
      contentType: photo.file!.type || undefined,
    })
  )
  if (uploadError) {
    throw new Error(`Не удалось загрузить файл в хранилище: ${formatError(uploadError)}`)
  }

  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('add_trainer_review_photo', {
      p_review_id: Number(reviewId),
      p_review_score_id: reviewScoreId ? Number(reviewScoreId) : null,
      p_storage_path: storagePath,
      p_original_name: photo.name,
      p_mime_type: photo.file?.type || photo.mimeType || null,
      p_size_bytes: photo.file?.size ?? photo.sizeBytes ?? null,
      p_width_px: photo.widthPx ?? null,
      p_height_px: photo.heightPx ?? null,
      p_sort_order: sortOrder,
    })
  )
  if (error) {
    throw new Error(`Файл загрузился, но не привязался к отсмотру: ${formatError(error)}`)
  }

  return {
    ...photo,
    id: `photo-${data}`,
    photoId: Number(data),
    reviewScoreId: reviewScoreId ? Number(reviewScoreId) : undefined,
    storageBucket: 'trainer-review-photos',
    storagePath,
    file: undefined,
  }
}

export async function uploadMyTrainerReviewEntryPhoto(params: {
  entryKey: string
  criterionCode?: string | null
  photo: TrainerChecklistPhoto
  sortOrder: number
}): Promise<TrainerChecklistPhoto> {
  const { entryKey, criterionCode, photo, sortOrder } = params
  if (!photo.file) return photo

  const supabase = getSupabase()
  const extension = photo.file.name.includes('.') ? photo.file.name.split('.').pop() : 'jpg'
  const storagePath = `entries/${entryKey}/${Date.now()}-${Math.random().toString(16).slice(2)}-${sanitizeFileName(photo.file.name || `photo.${extension}`)}`
  const { error: uploadError } = await withAuthRecovery(() =>
    supabase.storage.from('trainer-review-photos').upload(storagePath, photo.file!, {
      upsert: false,
      contentType: photo.file!.type || undefined,
    })
  )
  if (uploadError) throw new Error(`Не удалось загрузить файл в хранилище: ${formatError(uploadError)}`)

  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('add_my_trainer_review_entry_photo', {
      p_entry_key: entryKey,
      p_criterion_code: criterionCode ?? null,
      p_storage_path: storagePath,
      p_original_name: photo.name,
      p_mime_type: photo.file.type || photo.mimeType || null,
      p_size_bytes: photo.file.size ?? photo.sizeBytes ?? null,
      p_width_px: photo.widthPx ?? null,
      p_height_px: photo.heightPx ?? null,
      p_sort_order: sortOrder,
    })
  )
  if (error) throw new Error(`Файл загрузился, но не привязался к отсмотру: ${formatError(error)}`)
  return {
    ...photo,
    id: `photo-${data}`,
    photoKey: String(data),
    storageBucket: 'trainer-review-photos',
    storagePath,
    file: undefined,
  }
}

export async function deleteMyTrainerReviewEntryPhoto(
  entryKey: string,
  photo: TrainerChecklistPhoto
): Promise<void> {
  const supabase = getSupabase()
  if (photo.photoKey) {
    const { data, error } = await withAuthRecovery(() =>
      supabase.rpc('delete_my_trainer_review_entry_photo', {
        p_entry_key: entryKey,
        p_photo_key: photo.photoKey,
      })
    )
    if (error) throw new Error(formatError(error))
    if (!data) throw new Error('Фото не найдено или уже удалено')
  }
  if (photo.storageBucket && photo.storagePath) {
    const { error: storageError } = await withAuthRecovery(() =>
      supabase.storage.from(photo.storageBucket!).remove([photo.storagePath!])
    )
    if (storageError) throw new Error(formatError(storageError))
  }
}

export async function deleteTrainerReviewPhoto(photo: TrainerChecklistPhoto): Promise<void> {
  const supabase = getSupabase()

  if (photo.storageBucket && photo.storagePath) {
    const { error: storageError } = await withAuthRecovery(() =>
      supabase.storage.from(photo.storageBucket!).remove([photo.storagePath!])
    )
    if (storageError) throw new Error(formatError(storageError))
  }

  if (photo.photoId != null) {
    const { error } = await withAuthRecovery(() =>
      supabase.rpc('delete_trainer_review_photo', { p_photo_id: photo.photoId })
    )
    if (error) throw new Error(formatError(error))
  }
}
